/* Mantelly PDPs: videos inline tocam sozinhos (mudos, loop) quando ~50% visiveis,
   pausam ao sair; src so carrega perto da tela; um por vez; respeita reduced-motion.
   Thumbnails que abrem modal ganham previa muda inline; o clique continua abrindo com som. */
(() => {
  'use strict';
  if (!('IntersectionObserver' in window)) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  if (reduced.matches) return;

  const TRIGGERS = '[data-oil-play], [data-rl-video]';
  const SKIP = 'dialog video, [data-oil-hero-video], [data-no-autoplay]';
  const state = new WeakMap(); // video -> {ratio, userPaused, ours}
  let current = null;
  let userVideo = null; // video the visitor started with sound

  // "GIF" das descricoes: <video data-hc-preview> escrito no HTML (nao o criado por preview()).
  // Tocam juntos quando >=50% visiveis, fora da regra "um por vez" (varios lado a lado no desktop).
  const isGif = v => !!v.dataset.hcPreview && !v.classList.contains('hc-video-preview');
  const st = v => { if (!state.has(v)) state.set(v, {ratio: 0, userPaused: false, ours: false}); return state.get(v); };

  function load(v) {
    if (!v.getAttribute('src') && v.dataset.src) v.src = v.dataset.src;
    if (!v.getAttribute('src') && v.dataset.hcPreviewSrc) v.src = v.dataset.hcPreviewSrc;
  }

  function prep(v) {
    v.muted = true; v.defaultMuted = true;
    v.loop = true;
    v.playsInline = true;
    v.setAttribute('playsinline', ''); v.setAttribute('webkit-playsinline', '');
    if (!v.getAttribute('preload') || v.getAttribute('preload') === 'auto') v.preload = 'none';
  }

  function afterPlay(v) {
    const card = v.closest('.hs-video-card');
    const sound = card && card.querySelector('.hs-video-sound');
    if (sound) { sound.hidden = false; sound.textContent = v.muted ? 'Sound off' : 'Sound on'; sound.setAttribute('aria-label', v.muted ? 'Turn sound on' : 'Turn sound off'); }
    // Sem botao de som proprio: controles nativos para o visitante ligar o audio.
    if (!v.dataset.hcPreview && !v.closest('[data-hcb-ugc]') && !sound) v.controls = true;
  }

  async function play(v) {
    const s = st(v);
    if (current && current !== v) pause(current);
    current = v;
    load(v);
    if (!v.getAttribute('src') && !v.querySelector('source[src]')) return;
    if (v.readyState === 0 && v.preload === 'none') v.preload = 'metadata';
    s.ours = true;
    v.muted = true;
    try { await v.play(); afterPlay(v); }
    catch (e) { /* bloqueado: mantem botao play */ }
    finally { setTimeout(() => { s.ours = false; }, 0); }
  }

  function pause(v) {
    const s = st(v);
    if (v.paused) return;
    s.ours = true; v.pause();
    setTimeout(() => { s.ours = false; }, 0);
    if (current === v) current = null;
  }

  function playGif(v) {
    const s = st(v);
    load(v);
    if (v.readyState === 0 && v.preload === 'none') v.preload = 'metadata';
    s.ours = true; v.muted = true;
    v.play().catch(() => {}).finally(() => setTimeout(() => { s.ours = false; }, 0));
  }

  function pick() {
    if (userVideo && !userVideo.paused) return;
    let best = null, bestRatio = 0.5;
    document.querySelectorAll('video[data-hc-auto]').forEach(v => {
      if (isGif(v)) return;
      const s = st(v);
      if (s.userPaused) return;
      if (s.ratio >= bestRatio && (!best || s.ratio > bestRatio + 0.01)) { best = v; bestRatio = s.ratio; }
    });
    if (current && current !== best && st(current).ratio >= 0.5 && !current.paused) return; // mantem o atual enquanto visivel
    if (best && best !== current) play(best);
    else if (best && best.paused) play(best);
  }

  const near = new IntersectionObserver(entries => entries.forEach(e => {
    if (e.isIntersecting) { load(e.target); near.unobserve(e.target); }
  }), {rootMargin: '400px 0px'});

  const seen = new IntersectionObserver(entries => {
    entries.forEach(e => {
      const v = e.target, s = st(v);
      s.ratio = e.isIntersecting ? e.intersectionRatio : 0;
      if (isGif(v)) { if (s.ratio >= 0.5) { if (v.paused) playGif(v); } else pause(v); return; }
      if (s.ratio < 0.5) {
        if (v === current || (!v.paused && v !== userVideo)) pause(v);
        if (s.ratio === 0) s.userPaused = false;
      }
    });
    pick();
  }, {threshold: [0, 0.25, 0.5, 0.75, 1]});

  function watch(v) {
    if (v.dataset.hcAuto || v.matches(SKIP)) return;
    v.dataset.hcAuto = '1';
    prep(v);
    const s = st(v);
    v.addEventListener('pause', () => {
      if (!s.ours && !v.ended && document.visibilityState === 'visible' && s.ratio >= 0.5) s.userPaused = true;
      if (userVideo === v) userVideo = null;
    });
    v.addEventListener('play', () => {
      if (!s.ours) { s.userPaused = false; if (!v.muted) userVideo = v; }
    });
    v.addEventListener('volumechange', () => { if (!v.muted && !v.paused) userVideo = v; });
    near.observe(v);
    seen.observe(v);
  }

  function preview(btn) {
    if (btn.dataset.hcPreviewReady) return;
    const src = btn.dataset.oilPlay || btn.dataset.rlVideo;
    const img = btn.querySelector('img');
    if (!src || !img) return;
    btn.dataset.hcPreviewReady = '1';
    const v = document.createElement('video');
    v.className = 'hc-video-preview';
    v.dataset.hcPreview = '1';
    v.dataset.hcPreviewSrc = src;
    v.setAttribute('aria-hidden', 'true');
    v.tabIndex = -1;
    v.poster = img.currentSrc || img.src;
    v.addEventListener('playing', () => btn.classList.add('hc-preview-playing'));
    v.addEventListener('pause', () => btn.classList.remove('hc-preview-playing'));
    if (getComputedStyle(btn).position === 'static') btn.style.position = 'relative';
    img.insertAdjacentElement('afterend', v);
    watch(v);
  }

  // Um por vez, tambem com os players proprios das secoes e os modais.
  document.addEventListener('play', e => {
    const t = e.target;
    if (!(t instanceof HTMLVideoElement) || isGif(t)) return;
    document.querySelectorAll('video').forEach(o => { if (o !== t && !o.paused && o.dataset.hcAuto && !isGif(o)) pause(o); });
    if (t !== current && !t.dataset.hcPreview && !st(t).ours) { current = null; }
  }, true);
  document.addEventListener('visibilitychange', () => {
    document.querySelectorAll('video[data-hc-auto]').forEach(v => { if (isGif(v)) { if (document.hidden) pause(v); else if (st(v).ratio >= 0.5) playGif(v); } });
    if (document.hidden && current) pause(current); else if (!document.hidden) pick();
  });

  function init(scope) {
    const root = scope || document;
    root.querySelectorAll(TRIGGERS).forEach(preview);
    root.querySelectorAll('#MainContent video, main video').forEach(watch);
  }

  const style = document.createElement('style');
  style.textContent = '[data-hc-preview-ready]{isolation:isolate}' +
    '.hc-video-preview{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;pointer-events:none;border-radius:inherit;z-index:-1;opacity:0;transition:opacity .3s}' +
    '.hc-preview-playing>.hc-video-preview{opacity:1}.hc-preview-playing>img{visibility:hidden}';
  document.head.appendChild(style);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once: true});
  else init();
  document.addEventListener('shopify:section:load', e => init(e.target));
  reduced.addEventListener?.('change', () => { if (reduced.matches && current) pause(current); });
})();
